import React, { createContext, lazy, Suspense, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAssistant } from './useAssistant';

// The panel brings react-markdown with it; it loads when first opened, not with the application
const Panel = lazy(() => import('./Panel'));

// The signed-in person, or nothing: on the login page the global settings hold no user, and
// the `currentUser` selector of utils/selectors would read `.role` of undefined there
const selectUser = (state) => state.settings && state.settings.global && state.settings.global.currentUser;

const AssistantContext = createContext({ enabled: false, open: false, setOpen: () => {}, chat: null });

export const useAssistantContext = () => useContext(AssistantContext);

// FIN-1314: the conversation of the panel lives here, beside the routes, so it survives the
// person moving between pages; the button in every page's header opens the panel (Launcher),
// the panel itself unfolds from the top right. The chats page (/assistant) holds every
// conversation, so neither shows there.
export const AssistantProvider = ({ children }) => {
  const user = useSelector(selectUser);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const chat = useAssistant();
  const enabled = Boolean(user && user.ai_assistant) && !location.pathname.startsWith('/assistant');
  const value = useMemo(() => ({ enabled, open, setOpen, chat }), [enabled, open, chat]);

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {enabled && open && (
        <Suspense fallback={null}>
          <Panel chat={chat} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </AssistantContext.Provider>
  );
};

AssistantProvider.propTypes = { children: PropTypes.node };
AssistantProvider.defaultProps = { children: null };
